import { useAccount } from "#contexts/AccountContext"
import { getStandardRetryDelay, getStandardRetryLogic } from "#lib/errors"
import { queryKeys } from "#lib/query-keys"
import { showToast } from "#lib/toast"
import { homeFinancialSDK } from "#sdk/sdk"
import type {
  CreateExpense,
  ExpenseFilters,
  UpdateExpense,
} from "@home-finance-manager/shared/types/expenses"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import * as React from "react"

export function useExpenses(filters?: ExpenseFilters, includeSplits = false) {
  const { currentAccount } = useAccount()
  const filtersKey = React.useMemo(() => {
    if (!filters || Object.keys(filters).length === 0) {
      return "no-filters"
    }
    const sorted = Object.keys(filters)
      .sort()
      .reduce(
        (acc, key) => {
          const value = filters[key as keyof ExpenseFilters]
          if (value !== undefined && value !== null) {
            acc[key] = value
          }
          return acc
        },
        {} as Record<string, any>
      )
    return JSON.stringify(sorted)
  }, [filters?.search, filters?.categoryIds, filters?.recurringTypes, filters?.isVirtual])

  return useInfiniteQuery({
    queryKey: queryKeys.expenses.list(filtersKey, includeSplits),
    queryFn: ({ pageParam }) =>
      homeFinancialSDK.accounts.expenses.getExpenses({ ...filters, includeSplits }, pageParam, 10),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!currentAccount,
    refetchOnWindowFocus: false,
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

export function useExpense(id: number) {
  const { currentAccount } = useAccount()
  return useQuery({
    queryKey: queryKeys.expenses.detail(id),
    queryFn: () => homeFinancialSDK.accounts.expenses.getExpense(id),
    enabled: !!id && !!currentAccount,
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

export function useExpenseWithSplits(id: number) {
  const { currentAccount } = useAccount()
  return useQuery({
    queryKey: queryKeys.expenses.withSplits(id),
    queryFn: () => homeFinancialSDK.accounts.expenses.getExpenseWithSplits(id),
    enabled: !!id && !!currentAccount,
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

export function useCreateExpense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateExpense) => homeFinancialSDK.accounts.expenses.createExpense(data),
    onSuccess: (_, variables) => {
      // Reset all expense list queries to clear cached pages and force fresh fetch
      // This ensures the new expense appears in the table
      queryClient.resetQueries({ queryKey: queryKeys.expenses.all })
      // Invalidate dashboard to reflect the new expense
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all })
      showToast.success(`Expense "${variables.name}" created successfully`)
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

export function useUpdateExpense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateExpense }) =>
      homeFinancialSDK.accounts.expenses.updateExpense(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.expenses.all })
      await queryClient.cancelQueries({ queryKey: queryKeys.expenses.detail(id) })

      // Snapshot previous values
      const previousExpenses = queryClient.getQueriesData({
        queryKey: queryKeys.expenses.all,
      })
      const previousExpense = queryClient.getQueryData(queryKeys.expenses.detail(id))

      // Optimistically update all expense lists
      queryClient.setQueriesData({ queryKey: queryKeys.expenses.all }, (old: any) => {
        if (!old || !old.pages) return old
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.map((expense: any) =>
              expense.id === id
                ? { ...expense, ...data, updatedAt: new Date().toISOString() }
                : expense
            ),
          })),
        }
      })

      queryClient.setQueryData(queryKeys.expenses.detail(id), (old: any) => {
        if (!old) return old
        return { ...old, ...data, updatedAt: new Date().toISOString() }
      })

      return { previousExpenses, previousExpense }
    },
    onError: (_, variables, context) => {
      if (context?.previousExpenses) {
        context.previousExpenses.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      if (context?.previousExpense) {
        queryClient.setQueryData(queryKeys.expenses.detail(variables.id), context.previousExpense)
      }
    },
    onSuccess: (updatedExpense, variables) => {
      // Reset all expense list queries to clear cached pages and force fresh fetch
      // This ensures the updated expense appears correctly in the table
      queryClient.resetQueries({ queryKey: queryKeys.expenses.all })
      // Update the detail query with the response
      queryClient.setQueryData(queryKeys.expenses.detail(variables.id), updatedExpense)
      // Invalidate dashboard to reflect the updated expense
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all })
      showToast.success("Expense updated successfully")
    },
  })
}

export function useDeleteExpense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => homeFinancialSDK.accounts.expenses.deleteExpense(id),
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.expenses.all })

      // Snapshot previous value
      const previousExpenses = queryClient.getQueriesData({
        queryKey: queryKeys.expenses.all,
      })

      // Optimistically remove from all expense lists
      queryClient.setQueriesData({ queryKey: queryKeys.expenses.all }, (old: any) => {
        if (!old || !old.pages) return old
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.filter((expense: any) => expense.id !== id),
          })),
        }
      })

      return { previousExpenses }
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousExpenses) {
        context.previousExpenses.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
    },
    onSuccess: () => {
      // Reset all expense list queries to clear cached pages and force fresh fetch
      // This ensures the deleted expense is removed from the table
      queryClient.resetQueries({ queryKey: queryKeys.expenses.all })
      // Invalidate dashboard to reflect the deleted expense
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all })
      showToast.success("Expense deleted successfully")
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

export function useBulkCreateExpenses() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (expensesData: CreateExpense[]) =>
      homeFinancialSDK.accounts.expenses.bulkCreateExpenses(expensesData),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all })
      if (result.success > 0) {
        showToast.success(
          `Successfully created ${result.success} expense${result.success > 1 ? "s" : ""}`
        )
      }
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}
